/**
 * Per-insights-scope maps from attention / KPI ids to EduCore SPA paths.
 *
 * CampusOne does not invent operational screens for these roles — it deep-links into
 * EduCore when a launchUrl exists. Counsellor has no EduCore SPA today; their attention
 * rows stay inert rather than pointing at a fabricated welfare route.
 */
import { ATTENTION_EDUCORE_PATH, EduCorePaths, type EduCorePath } from './educoreLinks';

export type InsightsScope =
  | 'TEACHER'
  | 'STUDENT'
  | 'PARENT'
  | 'BURSAR'
  | 'LIBRARIAN'
  | 'REGISTRAR'
  | 'EXAM_OFFICER'
  | 'COUNSELLOR'
  | 'TRANSPORT'
  | 'ACADEMIC_OVERVIEW';

/** Default landing path when the panel offers a generic “Open in EduCore” action. */
export const SCOPE_HOME_PATH: Readonly<Partial<Record<InsightsScope, EduCorePath>>> = {
  TEACHER: EduCorePaths.teacherHome,
  STUDENT: EduCorePaths.studentHome,
  PARENT: EduCorePaths.parentDashboard,
  BURSAR: EduCorePaths.fees,
  LIBRARIAN: EduCorePaths.librarianOverview,
  REGISTRAR: EduCorePaths.registrarOverview,
  EXAM_OFFICER: EduCorePaths.exams,
  TRANSPORT: EduCorePaths.transportOverview,
  ACADEMIC_OVERVIEW: EduCorePaths.directorOverview,
};

export const TEACHER_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  OVERDUE_MARKING: EduCorePaths.teacherAssignments,
  AT_RISK_PUPILS: EduCorePaths.teacherAttendance,
  PENDING_REGISTERS: EduCorePaths.teacherAttendance,
};

export const STUDENT_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  ASSIGNMENTS_DUE: EduCorePaths.studentHomework,
  UPCOMING_EXAMS: EduCorePaths.studentExams,
  FEES_OUTSTANDING: EduCorePaths.studentFees,
  ATTENDANCE_BELOW: EduCorePaths.studentAttendance,
};

export const PARENT_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  CHILD_ATTENDANCE: EduCorePaths.parentReports,
  CHILD_FEES: EduCorePaths.parentFees,
};

export const REGISTRAR_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  SUSPENDED_STUDENTS: EduCorePaths.students,
  NEW_INTAKE: EduCorePaths.admissions,
};

export const EXAM_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  UPCOMING_EXAMS: EduCorePaths.exams,
  MARKS_GAP: EduCorePaths.grades,
};

export const LIBRARIAN_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  OVERDUE_LOANS: EduCorePaths.library,
};

export const TRANSPORT_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  OVER_BUDGET: EduCorePaths.transportManagement,
};

export const OVERVIEW_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  AT_RISK: EduCorePaths.attendance,
  FEES_OUTSTANDING: EduCorePaths.fees,
  SUSPENDED: EduCorePaths.students,
  // Executive-contract kinds. Anything not named here renders as text without a
  // link rather than pointing at a screen that does not exist (boarding has no
  // EduCore SPA route today, so BOARDING_UNRESOLVED stays deliberately inert).
  LOW_ATTENDANCE: EduCorePaths.attendance,
  ATTENDANCE_BELOW_THRESHOLD: EduCorePaths.attendance,
  OUTSTANDING_FEES: EduCorePaths.fees,
  UNASSIGNED_CLASS_GROUPS: EduCorePaths.classes,
  ADMISSIONS_PENDING: EduCorePaths.admissions,
  ADMISSIONS_STALLED: EduCorePaths.admissions,
};

export const COUNSELLOR_ATTENTION_PATH: Readonly<Record<string, EduCorePath>> = {
  // No welfare SPA exists; the students roll is the closest actionable screen.
  AT_RISK_PUPILS: EduCorePaths.students,
};

export const ATTENTION_PATH_BY_SCOPE: Readonly<
  Partial<Record<InsightsScope, Readonly<Record<string, EduCorePath>>>>
> = {
  TEACHER: TEACHER_ATTENTION_PATH,
  STUDENT: STUDENT_ATTENTION_PATH,
  PARENT: PARENT_ATTENTION_PATH,
  BURSAR: ATTENTION_EDUCORE_PATH,
  LIBRARIAN: LIBRARIAN_ATTENTION_PATH,
  REGISTRAR: REGISTRAR_ATTENTION_PATH,
  EXAM_OFFICER: EXAM_ATTENTION_PATH,
  COUNSELLOR: COUNSELLOR_ATTENTION_PATH,
  TRANSPORT: TRANSPORT_ATTENTION_PATH,
  ACADEMIC_OVERVIEW: OVERVIEW_ATTENTION_PATH,
};

export function attentionPathFor(
  scope: InsightsScope,
  attentionId: string,
): EduCorePath | undefined {
  return ATTENTION_PATH_BY_SCOPE[scope]?.[attentionId];
}
